import type { Types as OData } from 'shared/odata'

import clsx from 'clsx'
import { type FC, type ReactNode, useEffect, useMemo } from 'react'

import type { ActionsProps } from './types'

import { Checkbox } from '../Checkbox/Checkbox'
import css from './ContactsTable.module.css'
import { SelectedOutput } from './SelectedOutput'
import { useSelectedControls, type WithId } from './useSelectContorls'

interface Props {
	filter: Partial<OData.Page>
	total: number
}

export function SelectWrap<I extends WithId>({
	Actions,
	filter,
	Headers,
	items,
	Row,
	total
}: Props & {
	Actions: FC<ActionsProps>
	Headers: FC
	items: I[]
	Row: (props: I) => ReactNode
}) {
	const {
		areAllSelected,
		handleHeadCheck,
		handleRowCheck,
		handleSelectAll,
		isActiveHeader,
		removeId,
		selectAllRef,
		selected,
		unSelectAll
	} = useSelectedControls({ items })

	// biome-ignore lint/correctness/useExhaustiveDependencies: should reset on filter change
	useEffect(unSelectAll, [filter])

	const { amount, filters: changedFilters } = useModifyFilter({
		areAllSelected,
		filter,
		selected,
		total
	})

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const root = document.querySelector(`.${css.scroll}`)

		const rows = root?.querySelectorAll(`.${css.row}`)

		const observer = new IntersectionObserver(
			entries => {
				for (const entry of entries) {
					entry.target.classList.toggle(css.visibleRow, entry.isIntersecting)
				}
			},
			{
				root,

				rootMargin: '200px 0px 200px'
			}
		)

		rows?.forEach(row => observer.observe(row))

		return () => {
			root?.classList.remove(css.isRowChanging)
			observer.disconnect()
		}
	}, [items])

	return (
		<>
			<thead>
				<tr className={clsx(css.headerRow, isActiveHeader && css.activeHeader)}>
					<th className={css.headerCheck}>
						<Checkbox
							defaultChecked={false}
							name='select-all'
							onChange={handleHeadCheck}
							refAsProp={selectAllRef}
							value='selectAll'
						/>
					</th>

					{isActiveHeader ? (
						<SelectedOutput
							amount={amount}
							areAllSelected={areAllSelected}
							selectAll={handleSelectAll}
							total={total}
							unSelectAll={unSelectAll}
						>
							<Actions
								amount={amount}
								filters={changedFilters}
								removeId={removeId}
								unSelectAll={unSelectAll}
							/>
						</SelectedOutput>
					) : (
						<Headers />
					)}
				</tr>
			</thead>
			<tbody>
				{items.map(item => (
					<tr className={css.row} key={item.id}>
						<td className={css.select}>
							<Checkbox
								checked={areAllSelected || selected.has(item.id)}
								name='select-row'
								onChange={handleRowCheck}
								value={item.id}
							/>
						</td>
						<Row {...item} />
					</tr>
				))}
			</tbody>
		</>
	)
}

function useModifyFilter({
	areAllSelected,
	filter,
	selected,
	total
}: Props & {
	areAllSelected: boolean
	selected: Set<string>
}) {
	const amount = areAllSelected ? total : selected.size

	// biome-ignore lint/correctness/useExhaustiveDependencies: selected has stable ref
	const filters = useMemo(
		() =>
			areAllSelected
				? filter
				: {
						...filter,
						$filter: `id in (${Array.from(selected, id => `'${id}'`)})`
					},
		[areAllSelected, filter, selected.size]
	)

	return {
		amount,
		filters
	}
}
